import { DateTime } from 'luxon'
import type { LeakV0 } from '@willie/schemas'
import { HOUR_MS } from '~/utils/date'
import type { TRPCOutputs } from '~/utils/trpc'
import type { StationLeakV0Episode } from './useLeakV0Episodes'

export type StationLeakV0Series = TRPCOutputs['ops']['stations']['leakV0Series']

export type LeakV0ParamsHashEntry = StationLeakV0Series['paramsHashes'][number]

type LeakV0Bucket = StationLeakV0Series['buckets'][number]

// Buckets carry a viewer-zone ISO and episodes a raw UTC one: the same instant, never the same
// string. Everything the chart places is reduced to epoch milliseconds before it is compared.
export const toMillis = (iso: string): number => DateTime.fromISO(iso).toMillis()

export type LeakV0ChartRow = Omit<LeakV0Bucket, 'dateTime'> & {
  atMs: number
  noiseRibbonL: [number, number] | null
}

// `noiseBandL` is a half-width, so the ribbon is the baseline plus and minus it, not zero to it.
// The lower bound is clamped at zero: a negative volume means nothing, and the shared y-axis would
// scale to it and squeeze every series into the top of the plot.
const toRibbon = (baselineL: number | null, bandL: number | null): [number, number] | null =>
  baselineL === null || bandL === null ? null : [Math.max(baselineL - bandL, 0), baselineL + bandL]

// Rows carry the viewport, not just the axis domain: recharts widens an explicit numeric domain back
// over the data unless `allowDataOverflow` is set, so the rows are what bound a zoomed view.
const toChartRows = (buckets: LeakV0Bucket[], fromMs: number, toMs: number): LeakV0ChartRow[] =>
  buckets
    .map(({ dateTime, ...bucket }) => ({
      ...bucket,
      atMs: toMillis(dateTime),
      noiseRibbonL: toRibbon(bucket.frozenBaselineL, bucket.noiseBandL)
    }))
    .filter((row) => row.atMs >= fromMs && row.atMs <= toMs)

export type LeakV0BandKind = Exclude<LeakV0.LeakEpisodeState, 'OFF' | 'CLEAR'>

export interface LeakV0StateBand {
  key: string
  kind: LeakV0BandKind
  episodeId: string
  fromMs: number
  toMs: number
}

type BandSpan = Pick<LeakV0StateBand, 'kind' | 'fromMs' | 'toMs'>

// An open or non-advancing episode leaves the later columns null, so each band falls through to the
// next timestamp that exists and finally to the window's right edge; a band left with no span of its
// own collapses to zero width and the clamp below drops it.
const bandSpans = (episode: StationLeakV0Episode, windowEndMs: number): BandSpan[] => {
  const endMs = episode.clearedAt === null ? windowEndMs : toMillis(episode.clearedAt)
  const confirmedMs = episode.confirmedAt === null ? null : toMillis(episode.confirmedAt)
  const pendingMs = episode.pendingClearAt === null ? null : toMillis(episode.pendingClearAt)

  return [
    { kind: 'CANDIDATE', fromMs: toMillis(episode.candidateAt), toMs: confirmedMs ?? pendingMs ?? endMs },
    { kind: 'CONFIRMED', fromMs: confirmedMs ?? endMs, toMs: pendingMs ?? endMs },
    { kind: 'PENDING_CLEAR', fromMs: pendingMs ?? endMs, toMs: endMs }
  ]
}

/** Clamped to the window rather than filtered by containment: an episode may outlive it on both sides. */
const computeStateBands = (episodes: StationLeakV0Episode[], fromMs: number, toMs: number): LeakV0StateBand[] =>
  episodes.flatMap((episode) =>
    bandSpans(episode, toMs)
      .map((span) => ({
        key: `${episode.id}-${span.kind}`,
        kind: span.kind,
        episodeId: episode.id,
        fromMs: Math.max(span.fromMs, fromMs),
        toMs: Math.min(span.toMs, toMs)
      }))
      .filter((band) => band.toMs > band.fromMs)
  )

// Loudest state wins where episodes overlap: backfill and live episodes are persisted side by side.
const BAND_PRECEDENCE: LeakV0BandKind[] = ['CONFIRMED', 'PENDING_CLEAR', 'CANDIDATE']

export const bandKindAt = (bands: LeakV0StateBand[], atMs: number): LeakV0BandKind | null =>
  BAND_PRECEDENCE.find((kind) =>
    bands.some((band) => band.kind === kind && atMs >= band.fromMs && atMs < band.toMs)
  ) ?? null

export interface LeakV0TransitionMarker {
  key: string
  atMs: number
  episodeId: string
  event: LeakV0.LeakEpisodeEvent
}

const computeTransitionMarkers = (
  episodes: StationLeakV0Episode[],
  fromMs: number,
  toMs: number
): LeakV0TransitionMarker[] =>
  episodes.flatMap((episode) =>
    episode.events
      // Keyed by position, not by `bucketAt`: an anti-flap reopen emits two events at one instant.
      .map((event, index) => ({
        key: `${episode.id}-${index}`,
        atMs: toMillis(event.bucketAt),
        episodeId: episode.id,
        event
      }))
      .filter((marker) => marker.atMs >= fromMs && marker.atMs <= toMs)
  )

export interface LeakV0ParamsHashMarker {
  key: string
  atMs: number
  paramsHash: string
}

const computeParamsHashMarkers = (
  paramsHashes: StationLeakV0Series['paramsHashes'],
  fromMs: number,
  toMs: number
): LeakV0ParamsHashMarker[] =>
  paramsHashes
    // The first entry is the hash the window opens on, not a change within it.
    .slice(1)
    .map((entry) => ({ key: entry.paramsHash, atMs: toMillis(entry.firstSeenAt), paramsHash: entry.paramsHash }))
    .filter((marker) => marker.atMs >= fromMs && marker.atMs <= toMs)

export interface LeakV0SourceRun {
  key: string
  source: LeakV0.Source
  fromMs: number
  toMs: number
}

// The series is bucketed on a fixed 10-minute grid, so this stands in when a window holds too few
// buckets to measure a cadence from.
const FALLBACK_CADENCE_MS = 10 * 60_000

const estimateCadenceMs = (rows: LeakV0ChartRow[]): number => {
  const gaps = rows
    .slice(1)
    .map((row, index) => row.atMs - rows[index].atMs)
    .filter((gap) => gap > 0)
    .sort((a, b) => a - b)

  return gaps.length === 0 ? FALLBACK_CADENCE_MS : gaps[Math.floor(gaps.length / 2)]
}

/** Runs break on a source change and on any hole wider than half a bucket, so a hole stays a hole. */
const computeSourceRuns = (rows: LeakV0ChartRow[]): LeakV0SourceRun[] => {
  const cadenceMs = estimateCadenceMs(rows)

  return rows.reduce<LeakV0SourceRun[]>((runs, row) => {
    const open = runs.at(-1)
    const continues =
      open !== undefined && open.source === row.bucketSource && row.atMs - open.toMs <= cadenceMs / 2

    return continues
      ? [...runs.slice(0, -1), { ...open, toMs: row.atMs + cadenceMs }]
      : [
          ...runs,
          {
            key: `${row.bucketSource}-${row.atMs}`,
            source: row.bucketSource,
            fromMs: row.atMs,
            toMs: row.atMs + cadenceMs
          }
        ]
  }, [])
}

const TICK_STEP_HOURS = [1, 2, 3, 6, 12, 24, 48, 96]
const MAX_AXIS_TICKS = 12

// Calendar days, not 24h steps: an absolute step drifts off local midnight across a DST transition,
// and midnight is what carries the date on the axis.
const tickAt = (start: DateTime, index: number, stepHours: number): number =>
  (stepHours >= 24
    ? start.plus({ days: (index * stepHours) / 24 })
    : start.plus({ hours: index * stepHours })
  ).toMillis()

/**
 * Ticks anchored on local midnight rather than on bucket timestamps: recharts derives a number
 * axis' ticks from the data, which lands them on arbitrary instants a date label cannot read.
 */
const computeAxisTicks = (fromMs: number, toMs: number): number[] => {
  const spanHours = (toMs - fromMs) / HOUR_MS
  const stepHours = TICK_STEP_HOURS.find((step) => spanHours / step <= MAX_AXIS_TICKS) ?? spanHours
  const start = DateTime.fromMillis(fromMs).startOf('day')
  const count = Math.ceil((toMs - start.toMillis()) / (stepHours * HOUR_MS)) + 1

  return Array.from({ length: count }, (_, index) => tickAt(start, index, stepHours)).filter(
    (tick) => tick >= fromMs && tick <= toMs
  )
}

export interface LeakV0ChartModel {
  rows: LeakV0ChartRow[]
  rowsByTime: Map<number, LeakV0ChartRow>
  bands: LeakV0StateBand[]
  transitions: LeakV0TransitionMarker[]
  paramsChanges: LeakV0ParamsHashMarker[]
  sourceRuns: LeakV0SourceRun[]
  axisTicks: number[]
  fromMs: number
  toMs: number
  shaypAvailable: boolean
}

export const buildChartModel = (
  series: StationLeakV0Series,
  episodes: StationLeakV0Episode[],
  from: string,
  to: string
): LeakV0ChartModel => {
  const fromMs = toMillis(from)
  const toMs = toMillis(to)
  const rows = toChartRows(series.buckets, fromMs, toMs)

  return {
    rows,
    rowsByTime: new Map(rows.map((row) => [row.atMs, row])),
    bands: computeStateBands(episodes, fromMs, toMs),
    transitions: computeTransitionMarkers(episodes, fromMs, toMs),
    paramsChanges: computeParamsHashMarkers(series.paramsHashes, fromMs, toMs),
    sourceRuns: computeSourceRuns(rows),
    axisTicks: computeAxisTicks(fromMs, toMs),
    fromMs,
    toMs,
    shaypAvailable: series.shaypAvailable
  }
}

export type LeakV0PinnedTarget =
  | { kind: 'bucket'; row: LeakV0ChartRow }
  | { kind: 'transition'; marker: LeakV0TransitionMarker }
